import * as React from 'react'
// libs
import moment from 'moment'
import { useSelector } from 'react-redux'
// types
import { IObjectEntity, IPlan, IPlanItem } from 'types/graphql'
// constants
import { ENTITIES } from 'constants/entities'
import { TASKS_TYPES } from 'constants/home'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// utils
import { getFieldId } from 'utils/form'
import { get, isEmpty } from 'lodash'
import { isTaskOverdue } from './usePlanItemReviews'
import { generatePeriods } from 'utils/kpi'
import { map, find, pipe, filter, flatten } from 'lodash/fp'

export const usePlanItemsTasks = (planItems: IPlanItem[], plan: IPlan) => {
  const objectEntities = useSelector(objectEntitiesSelector)

  const generateAssignedPersons = entity => {
    const ownerPosition = get(entity, 'ownedByPosition')
    const ownerUser = get(entity, 'ownedByUser')

    if (!ownerPosition && !ownerUser) {
      return []
    }
    return [ownerPosition || ownerUser]
  }

  const generateTasksFromPeriods = planKPI => {
    return planKPI.periods
      .filter(period => period)
      .map(period => {
        const { id, currentPermissions, description, resultType, resultTypeScale, resultTarget, ranges, path } = planKPI

        return {
          id,
          path,
          ranges,
          resultType,
          description,
          resultTarget,
          resultTypeScale,
          currentPermissions,
          type: TASKS_TYPES.PERIOD,
          dueDate: period.dueDate,
          entityType: ENTITIES.PLAN_KPI,
          assignedPersons: generateAssignedPersons(planKPI),
          period: {
            ...period,
            startPeriod: period.startPoint,
            endPeriod: period.lastDayOfThePeriod
          },
          fieldId: pipe(
            find((item: IObjectEntity) => item.apiName === 'kpis'),
            objectEntity => getFieldId(get(objectEntity, 'fields'), 'resultTarget')
          )(objectEntities)
        }
      })
  }

  const planKPIs = React.useMemo(() => {
    return pipe(
      filter((planItem: IPlanItem) => !!planItem.planKPI && !isEmpty(planItem.planKPI.recurrence)),
      map(({ planKPI, path }: any) => ({ ...planKPI, path })),
      map(planKPI => {
        const { currentPeriods, capturedResultsOutOfRange, comingFirstPeriod, ranges } = generatePeriods(planKPI)
        const periods = [...currentPeriods, ...capturedResultsOutOfRange, comingFirstPeriod].filter(
          period => !isEmpty(period)
        )

        return { ...planKPI, periods, ranges }
      }),
      map(planKPI => generateTasksFromPeriods(planKPI)),
      flatten,
      map((planKPI: any) => ({
        ...planKPI,
        isOverdue: moment(planKPI.dueDate).isBefore(moment(), 'day'),
        isOpen: isEmpty(planKPI.period.capturedResult)
      }))
    )(planItems)
  }, [planItems])

  const generateTaskData = React.useCallback(() => {
    const generateTasks = ({
      apiName,
      planItems,
      entityKey,
      entityType
    }: {
      apiName: string
      planItems: IPlanItem[]
      entityKey: string
      entityType: string
    }) =>
      pipe(
        filter((planItem: IPlanItem) => !!planItem[entityKey] && planItem[entityKey].completionAt),
        map(({ [entityKey]: entity, path }: any) => ({
          id: entity.id,
          type: TASKS_TYPES.ACTION,
          action: { completionPercentage: entity.completionPercentage },
          isOpen: entity.completionPercentage !== 100,
          dueDate: moment(entity.completionAt),
          isOverdue: isTaskOverdue(entity.completionAt),
          entityType: entityType,
          description: entity.description,
          assignedPersons: generateAssignedPersons(entity),
          currentPermissions: entity.currentPermissions,
          fieldId: pipe(
            find((item: IObjectEntity) => item.apiName === apiName),
            objectEntity => getFieldId(get(objectEntity, 'fields'), 'completionPercentage')
          )(objectEntities),
          path
        }))
      )(planItems)

    return {
      planActions: generateTasks({
        planItems,
        apiName: 'actions',
        entityKey: 'planAction',
        entityType: ENTITIES.PLAN_ACTION
      }),
      planObjectives: generateTasks({
        planItems,
        apiName: 'objectives',
        entityKey: 'planObjective',
        entityType: ENTITIES.PLAN_OBJECTIVE
      })
    }
  }, [planItems])

  const { planActions, planObjectives } = generateTaskData()

  return {
    planKPIs,
    planActions,
    planObjectives
  }
}
